import React, { useState } from "react";
import { Search, Store, Star, Lock, Tag } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../i18n/LanguageContext";
import { api } from "../lib/api";
import { PageHeader } from "../components/shared";
import { ProductSearchBar } from "../components/ProductSearchBar";
import { demoTutorial } from "../i18n/demoTutorials";
import { demoCopy } from "../i18n/demoCopy";
import { demoMarket, demoTrack } from "./demoMode";
import { useDemoBuy } from "./useDemoBuy";

function DemoProductCard({ p, i, S }) {
  return (
    <div className="group flex flex-col overflow-hidden rounded-2xl border border-xmas-gold/15 bg-xmas-navy/40 transition hover:border-xmas-gold/40" data-testid={`demo-product-${i}`}>
      <div className="relative h-40 overflow-hidden bg-white/5"><img src={p.thumbnail} alt="" className="h-full w-full object-cover transition duration-500 group-hover:scale-105" /><span className="absolute left-3 top-3 rounded-full bg-xmas-navy/85 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-xmas-gold" data-testid={`demo-product-badge-${i}`}>{S.example}</span></div>
      <div className="flex flex-1 flex-col p-4">
        <p className="font-display text-base font-bold text-xmas-snow" data-testid={`demo-product-title-${i}`}>{p.title}</p>
        <p className="mt-1 text-xs text-xmas-champagne">{p.description}</p>
        <div className="mt-3 flex items-baseline gap-2"><span className="font-mono-num text-lg text-xmas-gold" data-testid={`demo-product-price-${i}`}>{p.price}</span>{p.old_price && <span className="font-mono-num text-xs text-xmas-slate line-through">{p.old_price}</span>}</div>
        <div className="mt-1 flex items-center gap-3 text-xs text-xmas-slate"><span className="inline-flex items-center gap-1"><Store className="h-3 w-3" />{p.source}</span><span className="inline-flex items-center gap-1"><Star className="h-3 w-3 fill-xmas-gold text-xmas-gold" />{p.rating}</span></div>
        <button onClick={() => { toast(S.demoClick); demoTrack("search_product_click", { title: p.title }); }} data-testid={`demo-product-view-${i}`} className="mt-4 inline-flex items-center justify-center gap-2 rounded-full border border-xmas-gold/40 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-xmas-gold transition hover:bg-xmas-gold/10"><Tag className="h-3.5 w-3.5" /> {S.view}</button>
      </div>
    </div>
  );
}

export default function DemoSearch() {
  const { lang } = useI18n();
  const T = demoTutorial(lang); const S = demoCopy(lang).search;
  const { buy, current } = useDemoBuy();
  const [state, setState] = useState({ loading: false, result: null, q: "" });
  const search = async (q) => {
    if (!q?.trim()) return;
    setState({ loading: true, result: null, q });
    demoTrack("search", { q });
    const { data } = await api.get(`/products/search?q=${encodeURIComponent(q)}&country=${demoMarket()}&limit=10`);
    setState({ loading: false, result: data, q });
  };
  const r = state.result;
  return (
    <div data-testid="demo-search-page">
      <PageHeader title={T.ui.searchT} subtitle={T.ui.searchSub} testid="demo-search-header" />
      <div className="glass rounded-2xl p-5 sm:p-6">
        <ProductSearchBar allowDemo testid="demo-search" historyKey="demo" onSearch={search} />
        <div className="mt-3 flex flex-wrap gap-2">{(T.ui.chips || []).map((c) => <button key={c} onClick={() => search(c)} data-testid={`demo-search-chip-${c}`} className="inline-flex items-center gap-1.5 rounded-full border border-xmas-gold/30 px-3 py-1.5 text-xs text-xmas-champagne transition hover:border-xmas-gold hover:text-xmas-gold"><Search className="h-3 w-3" /> {c}</button>)}</div>
      </div>
      {state.loading && <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{[0, 1, 2].map((i) => <div key={i} className="h-72 animate-pulse rounded-2xl border border-xmas-gold/10 bg-white/5" />)}</div>}
      {r && !state.loading && (
        <div className="mt-6" data-testid="demo-search-results">
          {r.demo_unprepared && (
            <div className="mb-5 flex flex-col gap-4 rounded-2xl border border-xmas-gold/40 bg-gradient-to-br from-xmas-gold/15 to-transparent p-5 sm:flex-row sm:items-center" data-testid="demo-search-unprepared">
              <Lock className="h-6 w-6 shrink-0 text-xmas-gold" />
              <p className="flex-1 text-sm text-xmas-champagne">{S.unprepared}</p>
              <button onClick={() => buy("search_unprepared")} data-testid="demo-search-unprepared-cta" className="btn-gold whitespace-nowrap rounded-full px-5 py-2.5 text-xs font-bold uppercase tracking-wider">{S.cta}{current?.display ? ` — ${current.display}` : ""}</button>
            </div>
          )}
          {!r.demo_unprepared && <p className="mb-4 text-xs uppercase tracking-[0.25em] text-xmas-gold/85" data-testid="demo-search-found">{S.found} “{state.q}”</p>}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{(r.products || []).map((p, i) => <DemoProductCard key={i} p={p} i={i} S={S} />)}</div>
        </div>
      )}
    </div>
  );
}
